import type { Editable, EditableValue, MasterTemplate } from "shared-types";
import type { GQLPaginatedDeliverable, GQLPagination } from "@/apollo/types";

import { apolloClient } from "@/apollo/index";
import paginatedDeliverablesQuery from "@/apollo/queries/v2/PaginatedDeliverables.gql";

import mainstore from "@/store";
import { CampaignGetters } from "@/store/modules/campaign";

import createPreflightStatusForDeliverables from "@/utils/Preflight/createPreflightStatusForDeliverables";
import type { ConstructedDeliverable } from "@/types/types";
import type { ConstructedLibraryFilters } from "@/utils/constructLibraryFiltersQueryVariables";

function constructDefaultValuesByEditableId(
    deliverable: GQLPaginatedDeliverable,
    storeEditables: Record<string, Editable>
) {
    /*
        We're temporarily (hopefully) going to tweak the deliverable
        data here to get it into a state where it's functional as
        a deliverable in the system as it was pre-BE generated deliverables.

        Why? So that we only need to change the minimal amount of code to
        get it working with the BE generated deliverables.
    */
    /*
        Default values are required for the current download implementation to
        work.

        It should be removed once we move to a batch download mechanism,
        because batch will fix everything.
    */
    return deliverable.computedValues.reduce<Record<string, EditableValue | null>>((acc, computedValue) => {
        const editable = storeEditables[computedValue.editableId];

        const defaultValue = editable.defaultValues.find(
            ({ masterTemplateId }) => masterTemplateId === deliverable.masterTemplate._id
        );

        if (defaultValue) {
            acc[computedValue.editableId] = defaultValue.value;
        }

        return acc;
    }, {});
}

function constructCombination(
    editableGroupValueIds: GQLPaginatedDeliverable["editableGroupValueIds"],
    storeGroups: Record<string, string>
) {
    /*
        `combination` is used in the QA drawer, in the library banner info poptip,
        to generate the reporting label and to construct the deliverable identifier.
    */
    return editableGroupValueIds.reduce<Record<string, { groupValueId: string }>[]>((acc, groupValueId) => {
        const group = storeGroups[groupValueId];

        if (group) {
            acc.push({
                [group]: {
                    groupValueId
                }
            });
        }

        return acc;
    }, []);
}

export function constructDeliverable(
    deliverable: GQLPaginatedDeliverable,
    storeEditables: Record<string, Editable>,
    storeGroups: Record<string, string>,
    storeMasterTemplates: Record<string, MasterTemplate>,
    hasCreativeXEnabled: boolean
): ConstructedDeliverable {
    return {
        ...deliverable,
        combination: constructCombination(deliverable.editableGroupValueIds, storeGroups),
        defaultValuesByEditableId: constructDefaultValuesByEditableId(deliverable, storeEditables),
        idHash: deliverable.hash,
        masterTemplate: storeMasterTemplates[deliverable.masterTemplate._id],
        preflight: createPreflightStatusForDeliverables(deliverable, hasCreativeXEnabled)
    };
}

export default async function getPaginatedDeliverables({
    campaignId,
    libraryFilters,
    page,
    deliverablesPerPage,
    store
}: {
    campaignId: string;
    libraryFilters: ConstructedLibraryFilters;
    page: number;
    deliverablesPerPage: number;
    store: typeof mainstore;
}) {
    const { data } = await apolloClient!.query<{
        computeDeliverables: {
            deliverables: GQLPaginatedDeliverable[];
            pagination: GQLPagination;
        };
    }>({
        fetchPolicy: "no-cache",
        query: paginatedDeliverablesQuery,
        variables: {
            campaignId,
            libraryFilters,
            page,
            perPage: deliverablesPerPage
        }
    });

    const editables = store.state.campaign.normalized.editables;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const groups = store.getters[CampaignGetters.groupByGroupValueId] as Record<string, string>;
    const masterTemplates = store.state.campaign.normalized.masterTemplates;
    const hasCreativeXEnabled = store.state.ui.currentClient?.creativeXPlatformSettings?.enabled ?? false;

    const deliverables = data.computeDeliverables.deliverables.map(deliverable => {
        return constructDeliverable(deliverable, editables, groups, masterTemplates, hasCreativeXEnabled);
    });

    return {
        deliverables,
        pagination: data.computeDeliverables.pagination
    };
}
